import { ApiError, apiErrorFromResponse, pathSegment } from '@/lib/api';
import { AGENT_URL, API_URL } from '@/lib/env';
import { handleApiError } from '@/lib/upgradeRequired';
import type { ChatMemoryList, ConversationPage, ConversationEntry, WriteOutcome } from '@/types/chat';

/** The origin an API URL names: its own when absolute, the page's when relative (`/api`). */
function originOf(apiUrl: string): string {
  const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  try {
    return new URL(apiUrl, base).origin;
  } catch {
    return base;
  }
}

/**
 * The chat's own backend: the unare-agent Worker (VER-540). `VITE_AGENT_URL` names it in every
 * real build; without it (dev with no Worker, the e2e preview, whose mocks answer any host) the
 * chat hangs off the api's origin, the same host a relative `VITE_API_URL` proxies through.
 */
export const CHAT_BASE_URL = AGENT_URL || originOf(API_URL);

/**
 * The thread's URL as TanStack AI's client speaks it: `POST` a turn, `GET ?threadId` the hydrate,
 * `GET ?runId&offset` a join. `threadId` names the conversation on every call, so the Worker finds
 * its object; the client adds its own query beside it.
 */
export function chatThreadUrl(conversationId: string): string {
  return `${CHAT_BASE_URL}/chat?threadId=${encodeURIComponent(conversationId)}`;
}

/** A failed chat request: the status and payload the Worker answered, for the bubble's reason. */
export class ChatRequestError extends Error {
  constructor(
    readonly apiError: ApiError,
    readonly retryAfter: string | null,
  ) {
    super(apiError.message);
    this.name = 'ChatRequestError';
  }
}

/**
 * The `fetch` the chat's client runs its turns through: a non-2xx answer becomes a
 * `ChatRequestError` carrying the Worker's body (the client would otherwise keep only the status),
 * and a 402 opens the upgrade dialog on the way, as every other call's 402 does.
 */
export async function chatFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const response = await fetch(input, init);
  if (response.ok) return response;
  const apiError = await apiErrorFromResponse(response);
  handleApiError(apiError);
  throw new ChatRequestError(apiError, response.headers.get('retry-after'));
}

async function request<T>(path: string, token: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${CHAT_BASE_URL}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  if (!response.ok) throw await apiErrorFromResponse(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** The person's conversations on the Worker (VER-539): the list, a rename, a delete, a stop. */
export const conversationsApi = {
  list(token: string, limit = 50, offset = 0): Promise<ConversationPage> {
    return request(`/agent/conversations?limit=${limit}&offset=${offset}`, token);
  },
  rename(token: string, id: string, title: string): Promise<ConversationEntry> {
    return request(`/agent/conversations/${pathSegment(id)}`, token, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    });
  },
  delete(token: string, id: string): Promise<void> {
    return request(`/agent/history/${pathSegment(id)}`, token, { method: 'DELETE' });
  },
  /** Stops a run in the Worker; the client's own `stop` only closes its side of the stream. */
  cancel(token: string, conversationId: string, runId: string): Promise<void> {
    return request(`/chat/cancel?threadId=${encodeURIComponent(conversationId)}`, token, {
      method: 'POST',
      body: JSON.stringify({ runId }),
    });
  },
};

/**
 * The person's answer on the card of a write the assistant proposed (VER-555): the Worker sends the
 * write it kept, confirmed, or drops it, and answers how it ended.
 */
export const writesApi = {
  answer(token: string, conversationId: string, toolCallId: string, confirmed: boolean): Promise<WriteOutcome> {
    return request(`/chat/confirm?threadId=${encodeURIComponent(conversationId)}`, token, {
      method: 'POST',
      body: JSON.stringify({ toolCallId, confirmed }),
    });
  },
};

/** What the chat remembers about the person on the Worker (VER-545): the list, and a forget. */
export const memoriesApi = {
  list(token: string): Promise<ChatMemoryList> {
    return request('/agent/memories', token);
  },
  delete(token: string, id: string): Promise<void> {
    return request(`/agent/memories/${pathSegment(id)}`, token, { method: 'DELETE' });
  },
};
